import { Component, Input, OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { PGAccommodation, PGAvailabilitySelection, PGAvailabilitySelectionLapse, PGBookingStatus, PGProductData, PGSupplierData, PGSurveyAnswers, PGTicketsSelection } from '../../../models/booking.model';
import { LocalizationService } from '../../../services/localization.service';
import { BookingService } from '../../../services/booking.service';
import { EnvironmentService } from '../../../services/environment.service';
import { PGLocation } from '../../../models/map.model';
import { PGUtilities } from '../../../utilities/pg-utilities';
import { GoogleMapsService } from '../../../services/google-maps.service';

@Component({
    selector: 'app-pg-booking-product',
    templateUrl: './pg-booking-product.component.html',
    styleUrls: ['./pg-booking-product.component.scss'],
    standalone: false
})
export class PgBookingProductComponent implements OnInit {

    @Input() status:PGBookingStatus;
    @Input() productData:PGProductData;
    @Input() supplierData:PGSupplierData;
    @Input() selectedAvailability:PGAvailabilitySelection|PGAvailabilitySelectionLapse;
    @Input() selectedTickets:PGTicketsSelection;
    @Input() selectedAccommodation:PGAccommodation;
    @Input() surveyAnswers:PGSurveyAnswers;
    @Input() paymentMethod:string;

    @Input() discountCode:string;
    @Input() discountValue:number;

    @Input() brokerCode:string;

    @Input() userName:string;
    @Input() userEmail:string;
    @Input() userPhone:string;

    @Input() isOptioned:boolean;

    @Input() selectedLanguage:string;
    @Input() additionalInformations:string;

    @Input() pid:string;

    @Input() compact:boolean|'never';

    constructor(private localizationService:LocalizationService, private bookingService:BookingService, private environmentService:EnvironmentService, private googleMapsService:GoogleMapsService) { }

    private _nowDate:Date = null;

    currentTimezone:string = null;

    ngOnInit(): void {
        this._nowDate = new Date();
        this.currentTimezone = DateTime.local().zoneName;
    }

    ngOnChanges() {
    }
    
    formatSelectedAvailabilityDate() {
        return this.localizationService.format.date(DateTime.fromFormat((this.selectedAvailability as PGAvailabilitySelection).date, 'yyyy-LL-dd').toJSDate())
    }

    formatSelectedAvailabilityTime() {
        return this.localizationService.format.time((this.selectedAvailability as PGAvailabilitySelection).begin)
    }

    formatSelectedAvailabilityLapseFrom() {
        return this.localizationService.format.time((this.selectedAvailability as PGAvailabilitySelection).begin)
    }

    formatSelectedAvailabilityLapseTo() {
        return this.localizationService.format.time((this.selectedAvailability as PGAvailabilitySelection).end)
    }

    formatSelectedAvailabilityDateFrom() {
        return this.localizationService.format.date(DateTime.fromFormat((this.selectedAvailability as PGAvailabilitySelectionLapse).from, 'yyyy-LL-dd').toJSDate())
    }
    
    formatSelectedAvailabilityDateTo() {
        return this.localizationService.format.date(DateTime.fromFormat((this.selectedAvailability as PGAvailabilitySelectionLapse).to, 'yyyy-LL-dd').toJSDate())
    }

    formatDiscountPrice() {
        if(this.discountValue == 100) return 'GRATIS';
        else {
            let _cPrice = this.getSelectedTicketsTotal() * (1 - this.discountValue / 100);
            _cPrice = Math.round(_cPrice * 100) / 100;
            return (_cPrice + ' ' + this.getCurrencySymbol())
        }
    }

    getSelectedTicketsTotal() {
        return this.bookingService.calculateSelectedTicketsTotal(this.selectedTickets);
    }

    getBrokerQuota() {
        return this.bookingService.calculateBrokerQuota(this.productData, this.selectedTickets, this.discountValue)
    }

    formatTicketName(name:string|{ [lang:string]: string }) {
        return this.localizationService.getTranslatedValue(name)
    }

    formatQuestionText(text:string|{ [lang:string]: string }) {
        return this.localizationService.getTranslatedValue(text)
    }

    formatPrice(val:number, useGratis?:boolean) {
        if(val == null) return null;
        else if(val == 0) {
            return useGratis ? 'GRATIS' : '';
        }
        else return val.toFixed(2) + ' ' + this.getCurrencySymbol();
    }

    formatAvailabilityInfo(info:string|{ [lang:string]: string }) {
        return this.localizationService.getTranslatedValue(info)
    }

    getBonificoCausale() {
        return this.localizationService.translate('pg-booking-product.payment-method-bonifico-causale') + ' ' + (this.productData.pid || this.productData.id) + ' ' + (this.userEmail || this.userPhone) + ' ' + this.formatSelectedAvailabilityDate() + ' ' + this.formatSelectedAvailabilityTime()
    }

    getLanguageLabel() {
        return this.localizationService.languageLabels[this.selectedLanguage]
    }

    getWebsiteURLClean() {
        if(this.environmentService.externalParameters.returnUrl != null) {
            return this.environmentService.externalParameters.returnUrl;
        }
    }

    getMeetingPointImage() {
        if(this.productData.meeting_point != null) {
            return this.localizationService.format.location(this.productData.meeting_point, { width: 600, height: 300 })
        }
    }

    private _lastMeetingPointLocation:PGLocation = null;
    private _lastMeetingPointAddress:string = null;

    formatMeetingPointAddress() {
        if(this._lastMeetingPointLocation == this.productData.meeting_point) return this._lastMeetingPointAddress;
        else {
            this._lastMeetingPointLocation = this.productData.meeting_point;
            this._lastMeetingPointAddress = null;

            if(this._lastMeetingPointLocation != null) {
                this.googleMapsService.loadMaps().then(() => {
                    let _geocoder = new google.maps.Geocoder()
            
                    try {
                        _geocoder.geocode({
                            location: {
                                lat: this._lastMeetingPointLocation.coordinates[0], 
                                lng: this._lastMeetingPointLocation.coordinates[1]
                            }
                        }).then((data) => {
                            if(data.results != null && data.results.length > 0) {
                                let _parsed = PGUtilities.parseAddressComponents(data.results[0].address_components)
                                this._lastMeetingPointAddress = '';

                                for(let _prop of ['address','locality','city','province','country']) {
                                    if(_parsed[_prop]) {
                                        if(this._lastMeetingPointAddress != '') this._lastMeetingPointAddress += ', '
                                        this._lastMeetingPointAddress += _parsed[_prop]
                                    }
                                }
                            }
                        })
                    }
                    catch(ex) {
                        console.log(ex)
                    }
                })
            }
        }
    }

    formatMeetingPointDescription() {
        let _result = null;
        let _fallback = null;

        for(let _translation of this.productData.translations) {
            if(_translation.meeting_point_description) {
                if(_translation.language == this.localizationService.currentLanguage) _result = _translation.meeting_point_description;
                if(_translation.language == this.localizationService.availableApplicationLanguages[0]) _result = _translation.meeting_point_description;
            }
        }

        if(_result) return _result
        else {
            if(!_fallback) _fallback = this.productData.translations[0]?.meeting_point_description
            if(!_fallback) _fallback = this.productData.meeting_point_description

            return _fallback
        }
    }

    getStatusColor() {
        switch(this.status) {
            case 'confirmed': return 'success';
            case 'canceled': return 'danger';
            case 'available': return 'primary';
            case 'pending': return 'secondary';
        }
    }

    showNotCancelableWarning() {
        if(this.productData.type == 'experience' && this.selectedAvailability != null) {
            let _cancellationDeadline:Date = null;
            
            if('from' in this.selectedAvailability) {
                _cancellationDeadline = new Date(this.selectedAvailability.from + 'T00:00:00')
            }
            else {
                _cancellationDeadline = new Date(this.selectedAvailability.date + 'T' + this.selectedAvailability.begin); 
            }

            _cancellationDeadline.setTime(_cancellationDeadline.getTime() - this.productData.cancellation_deadline * 1000 * 60 * 60 * 24);

            return _cancellationDeadline.getTime() < this._nowDate.getTime();
        }
    }

    getCurrencySymbol() {
        return this.localizationService.currencySymbols[this.productData?.currency || this.environmentService.getDefaultCurrency()]
    }

    isCumulativeBooking() {
        return /_cumulative\[/.test(this.productData.id)
    }
}
